import { Reducer } from 'redux';

import { assertNever } from '../../Utils/AssertNever';

import {
    FieldGuideActions,
    FieldGuideCompanySelection,
    FieldGuideConnectionState,
    FieldGuideEngagementSelection,
    FieldGuideUploadState,
    FieldGuideWorkbookMapping,
    KnownAction,
} from './Actions';

export type RequestMappings = { readonly [variantId: string]: FieldGuideWorkbookMapping };

export interface IFieldGuideState {
    connectionState: FieldGuideConnectionState;
    // The connected account's email/display name, from the connect response's fieldGuideEmail /
    // fieldGuideDisplayName. Both are cleared whenever the connection isn't Connected.
    fieldGuideEmail: string | undefined;
    fieldGuideDisplayName: string | undefined;
    // The server's snake_case reason the connection needs replacing. Undefined in every state except
    // ReconnectRequired, and undefined there too when the server gave no reason.
    reconnectReason?: string | undefined;
    // The upload flow's state. Optional and absent from defaultState: they exist only once BeginUpload has run.
    readonly uploadState?: FieldGuideUploadState | undefined;
    readonly selectedCompany?: FieldGuideCompanySelection | undefined;
    readonly selectedEngagement?: FieldGuideEngagementSelection | undefined;
    readonly requestMappings?: RequestMappings;
}

export const defaultState: IFieldGuideState = {
    connectionState: FieldGuideConnectionState.NotConnected,
    fieldGuideEmail: undefined,
    fieldGuideDisplayName: undefined,
    reconnectReason: undefined,
}

const PreviousSteps: { [step in FieldGuideUploadState]?: FieldGuideUploadState } = {
    [FieldGuideUploadState.SelectingEngagement]: FieldGuideUploadState.SelectingCompany,
    [FieldGuideUploadState.MappingRequests]: FieldGuideUploadState.SelectingEngagement,
    [FieldGuideUploadState.Reviewing]: FieldGuideUploadState.MappingRequests,
    [FieldGuideUploadState.Failed]: FieldGuideUploadState.Reviewing,
};

function nextStep(state: IFieldGuideState): FieldGuideUploadState | undefined {
    switch (state.uploadState) {
        case FieldGuideUploadState.SelectingCompany:
            return state.selectedCompany ? FieldGuideUploadState.SelectingEngagement : undefined;
        case FieldGuideUploadState.SelectingEngagement:
            return state.selectedEngagement ? FieldGuideUploadState.MappingRequests : undefined;
        case FieldGuideUploadState.MappingRequests:
            return FieldGuideUploadState.Reviewing;
        case undefined:
        case FieldGuideUploadState.Reviewing:
        case FieldGuideUploadState.Sending:
        case FieldGuideUploadState.Succeeded:
        case FieldGuideUploadState.Failed:
            return undefined;
        default:
            return assertNever(state.uploadState);
    }
}

function moveWhen(current: IFieldGuideState, from: FieldGuideUploadState, to: FieldGuideUploadState): IFieldGuideState {
    return current.uploadState === from ? { ...current, uploadState: to } : current;
}

// A changed company/engagement invalidates every chosen request, but the workbook files stay listed.
function clearMappedRequests(mappings: RequestMappings | undefined): RequestMappings {
    const source = mappings ? mappings : {};
    const cleared: { [variantId: string]: FieldGuideWorkbookMapping } = {};
    Object.keys(source).forEach(variantId => {
        cleared[variantId] = { request: undefined, fileName: source[variantId].fileName };
    });
    return cleared;
}

// The payload being sent was fixed at BeginSending; editing the selection now would desynchronise it from what is shown.
function selectionLocked(current: IFieldGuideState): boolean {
    return current.uploadState === FieldGuideUploadState.Sending;
}

export const reducer: Reducer<IFieldGuideState, KnownAction> = (state: IFieldGuideState | undefined, action: KnownAction): IFieldGuideState => {
    const current = state ? state : defaultState;

    switch (action.type) {
        case FieldGuideActions.BeginConnect: {
            return {
                ...current,
                connectionState: FieldGuideConnectionState.Connecting,
            };
        }
        case FieldGuideActions.ConnectSucceeded: {
            return {
                ...current,
                connectionState: FieldGuideConnectionState.Connected,
                fieldGuideEmail: action.fieldGuideEmail,
                fieldGuideDisplayName: action.fieldGuideDisplayName,
                reconnectReason: undefined,
            };
        }
        case FieldGuideActions.ConnectFailed: {
            return {
                ...current,
                connectionState: FieldGuideConnectionState.NotConnected,
                fieldGuideEmail: undefined,
                fieldGuideDisplayName: undefined,
                reconnectReason: undefined,
            };
        }
        case FieldGuideActions.SetReconnectRequired: {
            return {
                ...current,
                connectionState: FieldGuideConnectionState.ReconnectRequired,
                fieldGuideEmail: undefined,
                fieldGuideDisplayName: undefined,
                reconnectReason: undefined,
            };
        }
        case FieldGuideActions.ConnectionStatusLoaded: {
            const connected = action.status === 'Connected';
            const reconnectRequired = action.status === 'ReconnectRequired';
            return {
                ...(state ? state : defaultState),
                connectionState: FieldGuideConnectionState[action.status],
                fieldGuideEmail: connected ? action.fieldGuideEmail : undefined,
                fieldGuideDisplayName: connected ? action.fieldGuideDisplayName : undefined,
                reconnectReason: reconnectRequired ? action.reason : undefined,
            };
        }
        case FieldGuideActions.BeginUpload: {
            return {
                ...current,
                uploadState: FieldGuideUploadState.SelectingCompany,
                selectedCompany: undefined,
                selectedEngagement: undefined,
                requestMappings: {},
            };
        }
        case FieldGuideActions.CancelUpload: {
            return {
                ...current,
                uploadState: undefined,
                selectedCompany: undefined,
                selectedEngagement: undefined,
                requestMappings: {},
            };
        }
        case FieldGuideActions.SelectCompany: {
            if (selectionLocked(current)) {
                return current;
            }

            if (current.selectedCompany && current.selectedCompany.id === action.company.id) {
                return { ...current, selectedCompany: action.company };
            }

            return {
                ...current,
                selectedCompany: action.company,
                selectedEngagement: undefined,
                requestMappings: clearMappedRequests(current.requestMappings),
            };
        }
        case FieldGuideActions.SelectEngagement: {
            if (selectionLocked(current)) {
                return current;
            }

            if (current.selectedEngagement && current.selectedEngagement.id === action.engagement.id) {
                return { ...current, selectedEngagement: action.engagement };
            }

            return {
                ...current,
                selectedEngagement: action.engagement,
                requestMappings: clearMappedRequests(current.requestMappings),
            };
        }
        case FieldGuideActions.SetRequestMapping: {
            if (selectionLocked(current)) {
                return current;
            }

            return {
                ...current,
                requestMappings: {
                    ...current.requestMappings,
                    [action.variantId]: { request: action.request, fileName: action.fileName },
                },
            };
        }
        case FieldGuideActions.NextUploadStep: {
            const next = nextStep(current);
            return next ? { ...current, uploadState: next } : current;
        }
        case FieldGuideActions.PreviousUploadStep: {
            const previous = current.uploadState ? PreviousSteps[current.uploadState] : undefined;
            return previous ? { ...current, uploadState: previous } : current;
        }
        case FieldGuideActions.BeginSending: {
            return moveWhen(current, FieldGuideUploadState.Reviewing, FieldGuideUploadState.Sending);
        }
        case FieldGuideActions.SendSucceeded: {
            return moveWhen(current, FieldGuideUploadState.Sending, FieldGuideUploadState.Succeeded);
        }
        case FieldGuideActions.SendFailed: {
            return moveWhen(current, FieldGuideUploadState.Sending, FieldGuideUploadState.Failed);
        }
    }

    return current;
}
